// src/components/JobCenterPanel.tsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useJobCenter, JobRecord } from "../hooks/useJobCenter";
import { useNotificationContext } from "../contexts/NotificationContext";
import { useScrollLock } from "../hooks/useScrollLock";
import { readJsonLocal, writeJsonLocal } from "../utils/localStorageJson";
import { JobRow } from "./JobRow";
import { STATUS_CONFIG } from "./job-center.config";
import { JOB_KIND_META, JobKindMeta } from "./job-kind-meta";
import { JobEnqueueError } from "../services/jobService";
import { JobRetryError } from "../lib/jobs/jobRetry";
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
} from "lucide-react";

interface JobCenterPanelProps {
  onClose?: () => void;
  /** Scroll to and highlight this job on open. */
  focusJobId?: string | null;
}

type SortKey = "createdAt" | "status" | "type";
type Category = JobKindMeta["category"];

const CATEGORY_ORDER: Category[] = [
  "statements",
  "mission-orders",
  "emails",
  "messaging",
  "exports",
  "disciplinary",
  "accounting",
  "audit",
  "maintenance",
];

// CATEGORY_LABEL is built inside the component using t() — see useCategoryLabel()
const CATEGORY_LABEL_KEYS: Record<Category | "autres", string> = {
  statements: "job_center.category.statements",
  "mission-orders": "job_center.category.mission_orders",
  emails: "job_center.category.emails",
  messaging: "job_center.category.messaging",
  exports: "job_center.category.exports",
  disciplinary: "job_center.category.disciplinary",
  accounting: "job_center.category.accounting",
  audit: "job_center.category.audit",
  maintenance: "job_center.category.maintenance",
  autres: "job_center.category.autres",
};

const LS_COLLAPSED_KEY = "jobCenter.collapsedCategories";
const LS_GROUPED_KEY = "jobCenter.grouped";

const RETRY_CONCURRENCY = 4;

// A proper component for sortable headers
const SortableHeader: React.FC<{
  sortKey: SortKey;
  currentSort: SortKey;
  isDesc: boolean;
  onClick: (key: SortKey) => void;
  children: React.ReactNode;
  className?: string;
}> = React.memo(
  ({ sortKey, currentSort, isDesc, onClick, children, className }) => (
    <button
      onClick={() => onClick(sortKey)}
      className={`flex items-center gap-1 text-xs font-medium ${className}`}
    >
      {children}
      {currentSort === sortKey &&
        (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
    </button>
  )
);

const getCategory = (job: JobRecord): Category | "autres" => {
  const meta = JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
  return (meta?.category as Category | undefined) ?? "autres";
};

const matchesQuery = (job: JobRecord, q: string): boolean => {
  if (!q) return true;
  const meta = JOB_KIND_META[job.type as keyof typeof JOB_KIND_META];
  const haystack = [
    job.type,
    meta?.shortLabel,
    meta?.fullLabel,
    job.label,
    job.scope,
    (job.meta as any)?.scope,
    (job.meta as any)?.subject,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

export const JobCenterPanel: React.FC<JobCenterPanelProps> = ({
  onClose,
  focusJobId,
}) => {
  const { t } = useTranslation("management");
  const { showNotification } = useNotificationContext();
  const { ordered, remove, clearCompleted, retry, canRetry, stop, isClearing } = useJobCenter();
  // Both deletes can now be refused server-side (RLS, or a row still referenced).
  // They roll their optimistic removal back, so the operator must be told —
  // otherwise the list silently repopulates on the next reload.
  const removeJob = useCallback(
    async (id: string) => {
      try {
        await remove(id);
      } catch (e: any) {
        showNotification(e?.message ?? t("job_center.delete_failed"), "error");
      }
    },
    [remove, showNotification, t],
  );
  // Relance = nouvelle tâche via enqueue-job (droits vérifiés par
  // can_enqueue_job). Le refus doit se voir : la ligne d'origine reste alors
  // affichée, sinon l'opérateur croirait la tâche repartie.
  const retryErrorMessage = useCallback(
    (e: unknown) => {
      if (e instanceof JobEnqueueError && e.code === "JOB_FORBIDDEN") {
        return t("job_center.retry_forbidden");
      }
      if (e instanceof JobRetryError) return t("job_center.retry_unavailable");
      return t("job_center.retry_failed");
    },
    [t],
  );
  const retryJob = useCallback(
    async (id: string) => {
      try {
        await retry(id);
        showNotification(t("job_center.retry_started"), "success");
      } catch (e) {
        showNotification(retryErrorMessage(e), "error");
      }
    },
    [retry, retryErrorMessage, showNotification, t],
  );
  const clearCompletedJobs = useCallback(async () => {
    try {
      await clearCompleted();
    } catch (e: any) {
      showNotification(e?.message ?? t("job_center.clean_failed"), "error");
    }
  }, [clearCompleted, showNotification, t]);
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [isDesc, setIsDesc] = useState<boolean>(true);
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [grouped, setGrouped] = useState<boolean>(() =>
    readJsonLocal<boolean>(LS_GROUPED_KEY, true)
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set(readJsonLocal<string[]>(LS_COLLAPSED_KEY, []))
  );
  const [isRetryingAll, setIsRetryingAll] = useState(false);
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);

  // Guards the focus effect so it stops re-scrolling/re-highlighting once the
  // job has actually been located in the DOM. Deliberately NOT set merely for
  // having *seen* focusJobId: the two order mutations in
  // StadiumAuditPlannerContainer call the edge function directly, never go
  // through jobService.enqueueJob (the only path that dispatches an optimistic
  // `job:enqueue` add) and never invalidate the jobs query — so the only way a
  // brand-new job reaches `ordered` is the realtime subscription catching up
  // after the HTTP response already handed back `jobId`. The row may not exist
  // yet on the first run.
  const focusHandledRef = useRef<string | null>(null);

  // Focus a specific job: clear the filters that could hide it, expand its
  // category, scroll it into view and flash a ring. Re-runs on every `ordered`
  // change while not yet successful — clearing the search/filter again mid-race
  // is idempotent and harmless — until the row is actually found, at which
  // point focusHandledRef stops all further re-fires for this id.
  useEffect(() => {
    if (!focusJobId) return;
    if (focusHandledRef.current === focusJobId) return;

    setFilterActive(false);
    setQuery("");
    const meta = JOB_KIND_META[
      (ordered.find((j) => j.id === focusJobId)?.type ?? "") as keyof typeof JOB_KIND_META
    ];
    if (meta?.category) {
      setCollapsedCategories((prev) => {
        if (!prev.has(meta.category)) return prev;
        const next = new Set(prev);
        next.delete(meta.category);
        return next;
      });
    }
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-job-id="${focusJobId}"]`);
      if (!el) return; // not rendered yet — retry on the next `ordered` update
      focusHandledRef.current = focusJobId;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedJobId(focusJobId);
    });
    return () => cancelAnimationFrame(raf);
  }, [focusJobId, ordered]);

  // Bounded fallback: if the job never shows up (deleted, wrong user, or a
  // race that never resolves) give up after a few seconds instead of retrying
  // on every future `ordered` change for the rest of the session.
  useEffect(() => {
    if (!focusJobId) return;
    const timeout = window.setTimeout(() => {
      if (focusHandledRef.current !== focusJobId) {
        focusHandledRef.current = focusJobId;
      }
    }, 5000);
    return () => window.clearTimeout(timeout);
  }, [focusJobId]);

  // The highlight is a brief cue, not a selection.
  useEffect(() => {
    if (!highlightedJobId) return;
    const timer = setTimeout(() => setHighlightedJobId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedJobId]);

  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  // Lock background scroll through the shared counter-based manager so we don't
  // clobber another open overlay's lock (the bespoke capture/restore version
  // used to leave `overflow: hidden` stuck after close). The panel is only
  // mounted while open, so a constant `true` is correct here.
  useScrollLock(true);

  useEffect(() => {
    writeJsonLocal(LS_GROUPED_KEY, grouped);
  }, [grouped]);

  useEffect(() => {
    writeJsonLocal(LS_COLLAPSED_KEY, Array.from(collapsedCategories));
  }, [collapsedCategories]);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setIsDesc((d) => !d);
      } else {
        setSortKey(key);
        setIsDesc(true);
      }
    },
    [sortKey]
  );

  const toggleCollapseCategory = useCallback((cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const sortedJobs = useMemo(() => {
    const arr = ordered;
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = arr
      .filter((j) =>
        filterActive
          ? j.status === "pending" || j.status === "processing"
          : true
      )
      .filter((j) => matchesQuery(j, normalizedQuery));

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "createdAt":
          cmp = a.createdAt - b.createdAt;
          break;
        case "status":
          cmp =
            STATUS_CONFIG[a.status].priority - STATUS_CONFIG[b.status].priority;
          break;
        case "type":
          cmp = a.type.localeCompare(b.type);
          break;
      }
      return isDesc ? -cmp : cmp;
    });
  }, [ordered, sortKey, isDesc, filterActive, query]);

  const groupedJobs = useMemo(() => {
    if (!grouped) return null;
    const buckets = new Map<string, JobRecord[]>();
    for (const job of sortedJobs) {
      const cat = getCategory(job);
      const arr = buckets.get(cat) ?? [];
      arr.push(job);
      buckets.set(cat, arr);
    }
    const orderedKeys: string[] = [];
    for (const cat of CATEGORY_ORDER) {
      if (buckets.has(cat)) orderedKeys.push(cat);
    }
    // anything not in the canonical order (e.g. "autres") goes last
    for (const key of buckets.keys()) {
      if (!orderedKeys.includes(key)) orderedKeys.push(key);
    }
    return orderedKeys.map((cat) => ({
      cat,
      label: t(CATEGORY_LABEL_KEYS[cat as Category | "autres"] ?? cat),
      jobs: buckets.get(cat) ?? [],
    }));
  }, [sortedJobs, grouped]);

  // Seules les tâches relançables comptent : un chunk ou un PDF de tirage en
  // échec serait refusé par can_enqueue_job à chaque « Relancer tout ».
  const retryableJobs = useMemo(
    () => sortedJobs.filter((j) => canRetry(j)),
    [sortedJobs, canRetry]
  );
  const failedCount = retryableJobs.length;

  const handleRetryAllFailed = useCallback(async () => {
    if (isRetryingAll) return;
    const targets = [...retryableJobs];
    if (targets.length === 0) return;
    setIsRetryingAll(true);
    let started = 0;
    let lastError: unknown = null;
    try {
      // bounded concurrency to avoid hammering the retry endpoint
      const queue = [...targets];
      const workers = Array.from(
        { length: Math.min(RETRY_CONCURRENCY, queue.length) },
        async () => {
          while (queue.length > 0) {
            const job = queue.shift();
            if (!job) return;
            try {
              await retry(job.id);
              started += 1;
            } catch (e) {
              // On continue : un refus ne doit pas bloquer les autres.
              lastError = e;
            }
          }
        }
      );
      await Promise.all(workers);
    } finally {
      setIsRetryingAll(false);
    }
    if (started === targets.length) {
      showNotification(
        t("job_center.retry_all_done", { started, total: targets.length }),
        "success"
      );
    } else {
      showNotification(
        `${t("job_center.retry_all_partial", { started, total: targets.length })} ${retryErrorMessage(lastError)}`,
        started > 0 ? "warning" : "error"
      );
    }
  }, [retryableJobs, retry, isRetryingAll, showNotification, t, retryErrorMessage]);

  if (!portalTarget) {
    return null;
  }

  const totalCount = ordered.length;
  const visibleCount = sortedJobs.length;

  return createPortal(
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-x-0 top-12 sm:top-14 lg:top-16 bottom-0 bg-black/60 backdrop-blur-sm z-[10000]"
        onClick={onClose}
      />

      {/* Panel — sits above full-screen modals (GlassModal is z-[9999]) so the
          Job Center can overlay any open modal instead of hiding behind it. */}
      <div className="fixed end-2 top-12 sm:top-14 lg:top-16 w-[95vw] sm:w-[85vw] md:w-[720px] lg:w-[820px] xl:w-[920px] max-w-[95vw] max-h-[70vh] flex flex-col bg-[#1e293b] shadow-2xl border border-slate-700 rounded-lg overflow-hidden z-[10001] text-white">
        {/* Panel Header */}
        <div className="flex-shrink-0 px-2 sm:px-2.5 py-2 flex items-center justify-between gap-2 bg-[#0f172a] border-b border-slate-700">
          <h2 className="font-semibold text-xs sm:text-sm tracking-wide flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="hidden sm:inline">{t("job_center.title")}</span>
            <span className="sm:hidden">{t("job_center.title_short")}</span>
            <span
              className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-normal text-white/80 tabular-nums"
              title={
                visibleCount === totalCount
                  ? t("job_center.task_count", { count: totalCount })
                  : t("job_center.task_count_visible", { visible: visibleCount, total: totalCount })
              }
            >
              {visibleCount === totalCount
                ? totalCount
                : `${visibleCount}/${totalCount}`}
            </span>
          </h2>
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap justify-end">
            <div className="relative">
              <Search
                size={12}
                className="absolute start-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("job_center.search_placeholder")}
                aria-label={t("job_center.search_aria_label")}
                className="ps-5 pe-1.5 py-1 text-[10px] sm:text-xs rounded border bg-white/5 border-white/10 placeholder-white/30 text-white focus:outline-none focus:border-blue-400 w-28 sm:w-40"
              />
            </div>
            <button
              className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded border transition-colors ${
                filterActive
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-white/10 border-white/10 hover:bg-white/10"
              }`}
              onClick={() => setFilterActive((f) => !f)}
            >
              <span className="hidden md:inline">{t("job_center.active_only")}</span>
              <span className="md:hidden">{t("job_center.active_short")}</span>
            </button>
            <button
              className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded border transition-colors ${
                grouped
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-white/10 border-white/10 hover:bg-white/10"
              }`}
              onClick={() => setGrouped((g) => !g)}
              title={grouped ? t("job_center.grouped_view") : t("job_center.flat_view")}
            >
              {t("job_center.group")}
            </button>
            {failedCount > 0 && (
              <button
                className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded border transition-colors flex items-center gap-1 ${
                  isRetryingAll
                    ? "bg-white/10 border-white/20 text-white/80 cursor-wait"
                    : "bg-amber-600/20 border-amber-500/40 hover:bg-amber-600/30 text-amber-100"
                }`}
                onClick={handleRetryAllFailed}
                disabled={isRetryingAll}
                title={t("job_center.retry_failed_title", { count: failedCount })}
              >
                {isRetryingAll ? (
                  <span className="h-3 w-3 rounded-full border-2 border-t-transparent animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                <span className="hidden md:inline">{t("job_center.retry_all")}</span>
                <span className="md:hidden">{t("job_center.retry_short")}</span>
                <span className="opacity-70">({failedCount})</span>
              </button>
            )}
            <button
              className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded border transition-colors flex items-center gap-1 sm:gap-2 ${
                isClearing
                  ? "bg-white/10 border-white/20 text-white/80 cursor-wait"
                  : "bg-white/10 border-white/10 hover:bg-white/10"
              }`}
              onClick={() => {
                if (!isClearing) void clearCompletedJobs();
              }}
              disabled={isClearing}
            >
              {isClearing && (
                <span className="h-3 w-3 rounded-full border-2 border-t-transparent animate-spin" />
              )}
              {t("job_center.clean")}
            </button>
            {onClose && (
              <button
                className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-1 rounded border bg-white/10 border-white/10 hover:bg-white/10"
                onClick={onClose}
              >
                {t("job_center.close")}
              </button>
            )}
          </div>
        </div>

        {/* Table Headers */}
        <div className="flex-shrink-0 grid grid-cols-12 gap-1 sm:gap-2 px-2 sm:px-2.5 py-2 text-[10px] sm:text-[11px] uppercase tracking-wide text-slate-400 bg-[#0f172a] border-b border-slate-700">
          <div className="col-span-2">
            <SortableHeader
              sortKey="createdAt"
              currentSort={sortKey}
              isDesc={isDesc}
              onClick={handleSort}
            >
              <span className="hidden sm:inline">{t("job_center.col_start")}</span>
              <span className="sm:hidden">{t("job_center.col_date")}</span>
            </SortableHeader>
          </div>
          <div className="col-span-4">{t("job_center.col_task")}</div>
          <div className="col-span-3">
            <span className="hidden sm:inline">{t("job_center.col_progress")}</span>
            <span className="sm:hidden">{t("job_center.col_progress_short")}</span>
          </div>
          <div className="col-span-2">
            <SortableHeader
              sortKey="status"
              currentSort={sortKey}
              isDesc={isDesc}
              onClick={handleSort}
            >
              {t("job_center.col_status")}
            </SortableHeader>
          </div>
          <div className="col-span-1 text-end">
            <span className="hidden sm:inline">{t("job_center.col_actions")}</span>
            <span className="sm:hidden">{t("job_center.col_actions_short")}</span>
          </div>
        </div>

        {/* Jobs List */}
        <div className="flex-grow overflow-auto text-sm custom-scrollbars bg-[#1e293b]">
          {sortedJobs.length === 0 ? (
            <div className="p-3 text-center text-white/40 text-xs select-none">
              {query
                ? t("job_center.empty_search")
                : t("job_center.empty")}
            </div>
          ) : grouped && groupedJobs ? (
            groupedJobs.map((group) => {
              const isCollapsed = collapsedCategories.has(group.cat);
              return (
                <div key={group.cat}>
                  <button
                    type="button"
                    onClick={() => toggleCollapseCategory(group.cat)}
                    className="w-full sticky top-0 z-[1] flex items-center gap-2 px-2 sm:px-2.5 py-1 text-[10px] uppercase tracking-wider bg-[#0f172a]/95 backdrop-blur border-y border-slate-700 text-white/80 hover:bg-[#0f172a]"
                  >
                    {isCollapsed ? (
                      <ChevronRight size={12} className="rtl:rotate-180" />
                    ) : (
                      <ChevronDown size={12} />
                    )}
                    <span className="font-semibold">{group.label}</span>
                    <span className="text-white/50 tabular-nums">
                      ({group.jobs.length})
                    </span>
                  </button>
                  {!isCollapsed &&
                    group.jobs.map((job) => (
                      <div
                        key={job.id}
                        data-job-id={job.id}
                        className={
                          highlightedJobId === job.id
                            ? "rounded-md ring-2 ring-inset ring-primary-400/70 transition"
                            : undefined
                        }
                      >
                        <JobRow
                          job={job}
                          onRemove={removeJob}
                          onRetry={retryJob}
                          canRetry={canRetry(job)}
                          onStop={stop}
                          onClose={onClose}
                        />
                      </div>
                    ))}
                </div>
              );
            })
          ) : (
            sortedJobs.map((job) => (
              <div
                key={job.id}
                data-job-id={job.id}
                className={
                  highlightedJobId === job.id
                    ? "rounded-md ring-2 ring-inset ring-primary-400/70 transition"
                    : undefined
                }
              >
                <JobRow
                  job={job}
                  onRemove={removeJob}
                  onRetry={retryJob}
                  canRetry={canRetry(job)}
                  onStop={stop}
                  onClose={onClose}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    portalTarget
  );
};

export default JobCenterPanel;
